import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { DownloadIcon, FileTextIcon, PlayCircleIcon, CalendarIcon, ArrowLeftIcon, MessageCircleIcon, PhoneIcon } from 'lucide-react';
const sectionHeader = (tag: string, title: string, accent: string, description?: string) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.8
}} className="text-center mb-16" data-id="element-671">
    <p className="text-[#d4a017]/40 tracking-[0.3em] text-[10px] uppercase mb-5 font-light" data-id="element-672">
      {tag}
    </p>
    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#f5f0e8] mb-6 tracking-tight" data-id="element-673">
      {title} <span className="text-[#d4a017]" data-id="element-674">{accent}</span>
    </h2>
    <div className="w-16 h-px bg-gradient-to-r from-transparent via-[#d4a017]/30 to-transparent mx-auto mb-6" data-id="element-675"></div>
    {description && <p className="text-[#f5f0e8]/40 font-light max-w-2xl mx-auto leading-relaxed" data-id="element-676">
        {description}
      </p>}
  </motion.div>;
interface ResourceItem {
  title: string;
  description: string;
  type: 'video' | 'pdf' | 'gantt';
  buttonLabel: string;
}
const resources: ResourceItem[] = [{
  title: 'Video Explainer',
  description: 'Complete walkthrough of the immersive experience, portal system, and production vision.',
  type: 'video',
  buttonLabel: 'Watch Video'
}, {
  title: 'Sponsor Deck',
  description: 'Partnership tiers, brand visibility, hosted tables, and donor recognition opportunities.',
  type: 'pdf',
  buttonLabel: 'Download PDF'
}, {
  title: 'Tourism Deck',
  description: 'Ontario tourism relevance, cultural significance, and economic impact analysis.',
  type: 'pdf',
  buttonLabel: 'Download PDF'
}, {
  title: 'School Deck',
  description: 'Educational value, curriculum alignment, field trip format, and learning outcomes.',
  type: 'pdf',
  buttonLabel: 'Download PDF'
}, {
  title: 'F&B Deck',
  description: 'Culinary program, portal-by-portal menu, service philosophy, and dietary accommodations.',
  type: 'pdf',
  buttonLabel: 'Download PDF'
}, {
  title: 'Portal Deck',
  description: 'Deep dive into the five portals — characters, visuals, sensory design, and narrative structure.',
  type: 'pdf',
  buttonLabel: 'Download PDF'
}, {
  title: 'Production Gantt',
  description: 'Complete production schedule with workstreams, dependencies, and milestone dates.',
  type: 'gantt',
  buttonLabel: 'Download Schedule'
}, {
  title: 'Wedding Deck',
  description: 'Immersive wedding storyworlds — welcome night, sangeet, reception reveal, and luxury upsell.',
  type: 'pdf',
  buttonLabel: 'Download PDF'
}, {
  title: 'Investor Deck',
  description: 'Category creation, scalable platform, revenue streams, and global tour potential.',
  type: 'pdf',
  buttonLabel: 'Download PDF'
}, {
  title: 'Master Deck',
  description: 'The complete overview — all sections, all stakeholders, all opportunities in one document.',
  type: 'pdf',
  buttonLabel: 'Download PDF'
}];
/* ─── DOWNLOADS ─── */
export function DownloadsSection() {
  const getIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <PlayCircleIcon className="w-5 h-5" data-id="element-677" />;
      case 'gantt':
        return <CalendarIcon className="w-5 h-5" data-id="element-678" />;
      default:
        return <FileTextIcon className="w-5 h-5" data-id="element-679" />;
    }
  };
  return <section id="downloads" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-680">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/8 to-transparent" data-id="element-681"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-682">
        {sectionHeader('Section 19', 'Downloads &', 'Resources', 'Everything you need — decks, videos, schedules, and explainers.')}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-683">
          {resources.map((resource, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.04
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/40 p-6 flex flex-col" data-id="element-684">
              <div className="text-[#d4a017]/30 mb-3" data-id="element-685">
                {getIcon(resource.type)}
              </div>
              <h4 className="text-[#f5f0e8] text-sm font-medium mb-2" data-id="element-686">
                {resource.title}
              </h4>
              <p className="text-[#f5f0e8]/30 text-xs font-light leading-relaxed mb-4 flex-1" data-id="element-687">
                {resource.description}
              </p>
              <a href="#" className="inline-flex items-center gap-2 text-[#d4a017]/60 hover:text-[#d4a017] text-[10px] tracking-[0.15em] uppercase font-light transition-colors" data-id="element-688">
                <DownloadIcon className="w-3 h-3" data-id="element-689" />
                {resource.buttonLabel}
              </a>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
/* ─── FINAL CTA ─── */
export function FinalCTASection() {
  return <section className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-690">
      <div className="absolute inset-0" data-id="element-691">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#d4a017]/5 rounded-full blur-[200px]" data-id="element-692"></div>
      </div>
      <div className="container mx-auto px-6 relative z-10 max-w-3xl text-center" data-id="element-693">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} data-id="element-694">
          <p className="text-[#d4a017]/40 tracking-[0.3em] text-[10px] uppercase mb-6 font-light" data-id="element-695">
            Join the Vision
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#f5f0e8] mb-6 tracking-tight" data-id="element-696">
            Build the Kingdom
            <br data-id="element-697" />
            <span className="text-[#d4a017]" data-id="element-698">With Us</span>
          </h2>
          <div className="w-16 h-px bg-gradient-to-r from-transparent via-[#d4a017]/30 to-transparent mx-auto mb-8" data-id="element-699"></div>
          <p className="text-[#f5f0e8]/40 font-light leading-relaxed mb-12" data-id="element-700">
            Whether you're a sponsor, educator, planner, investor, or community
            leader — there is a place for you in this experience. Let's build
            something extraordinary together.
          </p>

          <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3" data-id="element-701">
            <motion.a href="https://wa.me/14169998778?text=I%20would%20like%20to%20book%20a%20review%20call%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#d4a017] hover:bg-[#b8890f] text-[#0a0a0f] px-6 py-3 text-xs tracking-[0.15em] uppercase font-medium transition-colors duration-300" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-702">
              <PhoneIcon className="w-3.5 h-3.5" data-id="element-703" />
              Book a Review Call
            </motion.a>
            <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20becoming%20a%20partner%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-[#d4a017]/30 hover:border-[#d4a017] text-[#f5f0e8]/60 hover:text-[#d4a017] px-6 py-3 text-xs tracking-[0.15em] uppercase font-light transition-all duration-300" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-704">
              <MessageCircleIcon className="w-3.5 h-3.5" data-id="element-705" />
              Become a Partner
            </motion.a>
            <a href="#downloads" className="inline-flex items-center gap-2 border border-[#d4a017]/30 hover:border-[#d4a017] text-[#f5f0e8]/60 hover:text-[#d4a017] px-6 py-3 text-xs tracking-[0.15em] uppercase font-light transition-all duration-300" data-id="element-706">
              <DownloadIcon className="w-3.5 h-3.5" data-id="element-707" />
              Download the Deck
            </a>
            <a href="#video-explainer" className="inline-flex items-center gap-2 border border-[#d4a017]/30 hover:border-[#d4a017] text-[#f5f0e8]/60 hover:text-[#d4a017] px-6 py-3 text-xs tracking-[0.15em] uppercase font-light transition-all duration-300" data-id="element-708">
              <PlayCircleIcon className="w-3.5 h-3.5" data-id="element-709" />
              Watch the Explainer
            </a>
          </div>

          <div className="mt-16" data-id="element-710">
            <Link to="/" className="inline-flex items-center gap-2 text-[#c2a878]/40 hover:text-[#d4a017] text-xs tracking-[0.15em] uppercase font-light transition-colors" data-id="element-711">
              <ArrowLeftIcon className="w-3.5 h-3.5" data-id="element-712" />
              Back to Main Site
            </Link>
          </div>
        </motion.div>
      </div>
    </section>;
}